import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import Button from '../components/Button'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SortableTableHead from '../components/SortableTableHead'
import ButtonRunDownload from '../components/ButtonRunDownload'
import DialogDeleteArbitro from '../components/DialogDeleteArbitro'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { selectArbitriSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetArbitriQuery,
  useLazyGetArbitroByIdQuery,
  useDeleteArbitroMutation,
  useDeleteDesignazioniByArbitroMutation,
} from '../hooks/api-slice'
import { setArbitro } from '../container/arbitri'

const Arbitri = () => {
  const { admin, designatore } = useSelector(state => state.home.infoUtente)
  const { isLoading } = useGetArbitriQuery(undefined, {
    skip: !(admin || designatore),
  })
  const arbitri = useSelector(selectArbitriSorted)

  const [triggerGetArbitroById] = useLazyGetArbitroByIdQuery()
  const [deleteArbitroMutation] = useDeleteArbitroMutation()
  const [deleteDesignazioniByArbitro] = useDeleteDesignazioniByArbitroMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()

  document.title = `${config?.name} - Arbitri`

  let columnsArbitri = [
    { label: 'Codice fiscale', sortable: true, sortingField: 'codiceFiscale' },
    { label: 'Cognome', sortable: true, sortingField: 'cognome' },
    { label: 'Nome', sortable: true, sortingField: 'nome' },
    { label: 'Domicilio', sortable: true, sortingField: 'cittaDomicilio' },
    { label: 'Stato', sortable: true, sortingField: 'status' },
  ]

  if (admin)
    columnsArbitri = [...columnsArbitri, { label: '', sortable: false }]

  const [openDeleteArbitro, setOpenDeleteArbitro] = useState()
  const [deletingArbitroId, setDeletingArbitroId] = useState()

  const handleClickOpenDeleteArbitro = id => {
    setDeletingArbitroId(id)
    setOpenDeleteArbitro(true)
  }

  const handleCloseDeleteArbitro = () => {
    setOpenDeleteArbitro()
    setDeletingArbitroId()
  }

  const editArbitro = async id => {
    const result = await triggerGetArbitroById(id)
    if (!result?.error) {
      dispatch(setArbitro(result?.data))
      navigate('/arbitro')
    }
  }

  const deleteArbitro = async (id, deleteAlsoDesignazioni) => {
    if (admin && deleteAlsoDesignazioni) {
      const result = await deleteDesignazioniByArbitro(id)
      if (result?.error) {
        setFeedback("Errore durante l'eliminazione delle designazioni.")
        return
      }
    }

    const result = await deleteArbitroMutation(id)
    if (!result?.error) {
      setFeedback('Arbitro eliminato correttamente.', false)
      handleCloseDeleteArbitro()
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Arbitri' />
      <Grid container sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          {admin && (
            <center>
              <Button
                to='/arbitro'
                onClick={() => dispatch(setArbitro())}
                children='Nuovo arbitro'
              />
            </center>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ButtonRunDownload
            buttonText='Scarica Excel'
            algorithm='10_GenerateExcelArbitri'
            type='XLSX'
            disabled={!arbitri?.length}
          />
        </Grid>
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!arbitri && <GridHeader text='Elenco degli arbitri non trovato.' />) ||
        (arbitri.length === 0 && (
          <GridHeader text='Non è ancora stato creato nessun arbitro.' />
        )) || (
          <Grid container paddingY={2} sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={arbitri}
                  columns={columnsArbitri}
                  tableName='arbitri'
                />
                <TableBody>
                  {arbitri.map((a, index) => {
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: colours.white,
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                        onClick={() => editArbitro(a.id)}
                      >
                        <TableCell>{a.codiceFiscale}</TableCell>
                        <TableCell>{a.cognome}</TableCell>
                        <TableCell>{a.nome}</TableCell>
                        <TableCell>{a.cittaDomicilio}</TableCell>
                        <TableCell>
                          <Chip
                            variant={
                              a.status === 'complete' || !a.isActive
                                ? 'outlined'
                                : 'filled'
                            }
                            label={
                              (!a.isActive && 'inattivo') ||
                              (a.status === 'complete' && 'completo') ||
                              (a.status === 'incomplete' && 'da completare') ||
                              'sconosciuto'
                            }
                            color={
                              (!a.isActive && 'error') ||
                              (a.status === 'complete' && 'success') ||
                              (a.status === 'incomplete' && 'warning') ||
                              'default'
                            }
                          />
                        </TableCell>
                        {admin && (
                          <TableCell>
                            <GridIconButton
                              type='delete'
                              onClick={e => {
                                e.stopPropagation()
                                handleClickOpenDeleteArbitro(a._id)
                              }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <DialogDeleteArbitro
        open={openDeleteArbitro}
        handleClose={handleCloseDeleteArbitro}
        triggerFunction={deleteArbitro}
        idArbitro={deletingArbitroId}
      />
    </Grid>
  )
}

export default Arbitri
